import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyOtpApi } from "../model/authApi";
import { sendOtpApi } from "../model/authApi";
import { updateUserProfileApi } from "../model/authApi";
import { routes } from "@/app/navigation/routes";
import { tokenService } from "@/services/auth/token.service";
import { useAuth } from "@/app/providers/useAuth";
import { isUserEligibleForPersistentAuthToken } from "../utils/auth.utils";
import type { User } from "../model/authTypes";
import { SESSION_PHONE_MOBILE_INPUT_PREFILL } from "../constants/sessionStorageKeys";

export const usePhoneVerificationViewModel = () => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const validateOtp = () => {
    if (!otp) {
      return "OTP is required";
    }

    if (!/^[0-9]+$/.test(otp)) {
      return "Only numbers allowed";
    }

    if (otp.length !== 6) {
      return "OTP must be 6 digits";
    }

    return "";
  };

  const handleResendOtp = async () => {
    try {
      setError("");

      const phone = sessionStorage.getItem("phone");

      if (!phone) {
        setError("Phone not found. Please retry.");
        return;
      }

      await sendOtpApi({ phone });
    } catch (error: unknown) {
      setError("Failed to resend OTP. Please try again.");
    }
  };

  const handleVerify = async () => {
    const validationError = validateOtp();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setError("");

      const phone = sessionStorage.getItem("phone");

      if (!phone) {
        setError("Phone not found. Please retry.");
        return;
      }

      const response = await verifyOtpApi({
        phone,
        otp,
      });

      console.log("response123", response);

      // ✅ Check success
      if (response.status !== "success") {
        setError("OTP verification failed");
        return;
      }

      const flow = sessionStorage.getItem("otpFlow");

      if (flow === "signup") {
        navigate(routes.login);
        return;
      } else if (flow === "login") {
        try {
          // ✅ Mark locally
          sessionStorage.setItem("isPhoneVerified", "true");

          const tempToken = sessionStorage.getItem("temp_token");

          const res = await updateUserProfileApi(
            {
              is_phone_verified: true,
              phone,
              force_mobile_update: false,
            },

            "inline",
            undefined,
            tempToken || ""
          );

          // ✅ Check response
          if (res.status !== "success") {
            setError("Profile update failed");
            return;
          }

          const otpUser = response.data?.user as User | undefined;
          const mergedUser: User | null = res.data
            ? res.data
            : otpUser
              ? { ...otpUser, is_phone_verified: true }
              : null;

          if (!mergedUser) {
            setError("Could not load user profile. Please try again.");
            return;
          }

          if (isUserEligibleForPersistentAuthToken(mergedUser)) {
            const bearerFromApi =
              typeof res.temp_token === "string" && res.temp_token.trim().length > 0
                ? res.temp_token.trim()
                : null;
            const bearerRaw = bearerFromApi ?? tempToken;
            const bearer =
              typeof bearerRaw === "string" && bearerRaw.trim().length > 0
                ? bearerRaw.trim()
                : null;
            if (!bearer) {
              console.error(
                "[auth] OTP login flow: eligible user but no non-empty bearer (API temp_token and session temp_token missing or blank)"
              );
              setError("Session expired. Please sign in again.");
              navigate(routes.login, { replace: true });
              return;
            }
            tokenService.setToken(bearer);
            if (!tokenService.hasToken()) {
              console.error(
                "[auth] OTP login flow: setToken did not yield auth_token; leaving temp_token in session for retry"
              );
              setError("Session expired. Please sign in again.");
              navigate(routes.login, { replace: true });
              return;
            }
            sessionStorage.removeItem("temp_token");
            login(mergedUser);

            // Priority 1: protest flow restoration via sessionStorage.
            const savedProtestFlow = sessionStorage.getItem("property_protest_flow");
            if (savedProtestFlow) {
              try {
                const parsedFlow = JSON.parse(savedProtestFlow);
                sessionStorage.removeItem("property_protest_flow");
                navigate(routes.properties, { replace: true, state: parsedFlow });
                return;
              } catch (parseError) {
                console.error("Failed to parse property protest flow:", parseError);
                sessionStorage.removeItem("property_protest_flow");
              }
            }

            // Priority 2: return user to the page they came from.
            const fromState = (location.state as { from?: string } | null | undefined)?.from;
            if (typeof fromState === "string" && fromState && fromState !== routes.login) {
              navigate(fromState, { replace: true });
              return;
            }

            // Priority 3: default landing.
            navigate(routes.home, { replace: true });
            return;
          }

          if (mergedUser.force_mobile_update) {
            sessionStorage.setItem("otpFlow", "login");
            sessionStorage.setItem("phone", mergedUser.phone || "");
            const phone = mergedUser.phone?.trim() ?? "";
            if (phone) {
              sessionStorage.setItem(SESSION_PHONE_MOBILE_INPUT_PREFILL, phone);
            }
            navigate(routes.phoneMobileInput);
            return;
          }

          if (mergedUser.force_password_update) {
            navigate(routes.forcePasswordUpdate, {
              state: { email: mergedUser.email },
            });
            return;
          }

          if (!mergedUser.phone?.trim()) {
            navigate(routes.phoneMobileInput);
            return;
          }

          if (!mergedUser.is_phone_verified) {
            sessionStorage.setItem("phone", mergedUser.phone);
            navigate(routes.phoneVerification);
            return;
          }

          // if (!mergedUser.is_mail_verified) {
          //   navigate(routes.emailVerificationRequired, {
          //     state: { email: mergedUser.email },
          //   });
          //   return;
          // }

          setError("Unable to continue. Please sign in again.");
          navigate(routes.login, { replace: true });
        } catch (err: unknown) {
          setError("Failed to update profile. Please try again.");
          return;
        }
      } else {
        setError("please try again");
      }
    } catch (error: unknown) {
      setError("Invalid OTP. Please try again.");
    }
  };

  return {
    otp,
    setOtp,
    handleVerify,
    handleResendOtp,
    error,
  };
};
